import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ActiveTrade } from '../components/dashboard/ActiveTrade';
import { HeroBalance } from '../components/dashboard/HeroBalance';
import { PriceToBeat } from '../components/dashboard/PriceToBeat';
import {
  useBotToggle,
  useMe,
  useMyBalances,
  usePositions,
} from '../hooks/useApiQuery';
import type { UserProfile } from '../lib/types';

interface Props {
  welcomeUser?: UserProfile | null;
}

export function Dashboard({ welcomeUser }: Props) {
  const { t } = useTranslation();
  // Three-tile balances (main wallet + PM/HL bot bankrolls). Same query
  // the Wallet page header uses — TanStack dedups across mounts.
  const balances = useMyBalances();
  const positions = usePositions();
  const me = useMe();
  const toggleBot = useBotToggle();

  const closed =
    positions.data?.items?.filter((p) => p.status === 'closed') ?? [];
  // Render one card per open position — a user can run multiple
  // PM markets + multiple HL perps concurrently. The legacy "first
  // open only" path predated both multi-market PM and HL.
  const openPositions =
    positions.data?.items?.filter(
      (p) => p.status === 'open' || p.status === 'pending',
    ) ?? [];

  const pnlToday = useMemo(() => {
    const sinceMs = Date.now() - 24 * 3600 * 1000;
    return closed.reduce((acc, p) => {
      const ts = p.closed_at ? Date.parse(p.closed_at) : 0;
      if (ts >= sinceMs) return acc + Number(p.realized_pnl_usdc ?? 0);
      return acc;
    }, 0);
  }, [closed]);

  return (
    <div className="mx-auto max-w-app space-y-4 px-5 pt-5">
      {welcomeUser ? (
        <div className="pb-eyebrow pb-fade-up">
          {welcomeUser.tg_first_name
            ? t('auth.welcome_back', { name: welcomeUser.tg_first_name })
            : t('auth.welcome_new')}
        </div>
      ) : null}

      <div className="pb-fade-up" style={{ animationDelay: '40ms' }}>
        <HeroBalance
          me={me.data ?? null}
          balances={balances.data}
          positions={positions.data}
          pnlToday={pnlToday}
          isLoading={balances.isLoading || positions.isLoading}
          toggleBusy={toggleBot.isPending}
          onToggleBot={(patch) => toggleBot.mutate(patch)}
        />
      </div>

      {/* Unified "Bot Cove" — one PriceToBeat card hosts the multi-
          market chip strip + countdown + scanning/trading body for the
          selected PM market. When that market has an open trade, the
          body becomes a headless ActiveTrade rather than the PTB price
          grid; the OceanStrip footer reflects the mode (hunting /
          guarding). HL positions don't have a PM round/chip context,
          so they render below as standalone ActiveTrade cards. */}
      <div className="pb-fade-up space-y-3" style={{ animationDelay: '140ms' }}>
        <PriceToBeat openPositions={openPositions} />
        {openPositions
          .filter((p) => p.bot_kind !== 'polymarket')
          .map((p) => (
            <ActiveTrade key={p.id} position={p} />
          ))}
      </div>
    </div>
  );
}
